import { noop } from "lodash";
import React, {
  createContext,
  FC,
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Subject } from "rxjs";
import { ShellContext } from "../../pages/shell/context";
import { IStepperContext, IWrapWithStepperContextProps } from "./models";

/**
 * This hook provides an interface and state for the stepper
 * @param param0 onNExtValidation is validation carried out when stepper goes to next
 * @returns controls for the stepper context, i.e. next, back, reset, activeStep
 */
function useStepper({
  initialActiveStep = 0,
}: IWrapWithStepperContextProps): IStepperContext {
  const { contentContainerRef } = useContext(ShellContext);
  const [activeStep, setActiveStep] = useState(initialActiveStep);
  const [isNextEnabled, setIsNextEnabled] = useState(true);
  const [isBackEnabled, setIsBackEnabled] = useState(true);
  const [validation, setValidation] = useState<() => Promise<void>>(() => () =>
    Promise.resolve()
  );

  const next = useMemo<Subject<SyntheticEvent<any> | undefined>>(
    () => new Subject(),
    []
  );
  const back = useMemo<Subject<SyntheticEvent<any> | undefined>>(
    () => new Subject(),
    []
  );
  const reset = useMemo<Subject<SyntheticEvent<any> | undefined>>(
    () => new Subject(),
    []
  );

  useEffect(() => {
    const foo = next.subscribe((e: SyntheticEvent<any> | undefined) => {
      if (e) e.preventDefault();
      validation()
        .then(() => {
          setActiveStep((prevActiveStep) => prevActiveStep + 1);
        })
        .catch(noop);
    });

    return () => foo.unsubscribe();
  }, [validation, next, setActiveStep]);

  useEffect(() => {
    const foo = back.subscribe((e: SyntheticEvent<any> | undefined) => {
      if (e) e.preventDefault();
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    });

    return () => foo.unsubscribe();
  }, [back, setActiveStep]);

  useEffect(() => {
    const foo = reset.subscribe((e: SyntheticEvent<any> | undefined) => {
      if (e) e.preventDefault();
      setActiveStep(0);
    });

    return () => foo.unsubscribe();
  }, [reset, setActiveStep]);

  useEffect(() => {
    if (contentContainerRef && contentContainerRef.current) {
      contentContainerRef.current.scrollTo(0, 0);
    }
  }, [activeStep]);

  return {
    back,
    next,
    reset,
    activeStep,
    isNextEnabled,
    setIsNextEnabled,
    isBackEnabled,
    setIsBackEnabled,
    setValidation,
  };
}

export const StepperContext = createContext<IStepperContext>({
  activeStep: 0,
  back: new Subject(),
  next: new Subject(),
  reset: new Subject(),
  setIsNextEnabled: noop,
  isNextEnabled: false,
  setIsBackEnabled: noop,
  isBackEnabled: false,
  setValidation: () => Promise.resolve(),
});

export const WrapWithStepperContext: FC<IWrapWithStepperContextProps> = (
  props
) => {
  const value = useStepper(props);
  return (
    <StepperContext.Provider value={value}>
      {props.children}
    </StepperContext.Provider>
  );
};
